/**
 * Edit Shift Modal Component
 * Shared Material Dialog for editing labor/shift entries.
 * Allows editing clock in/out times, notes, clocking out, and deleting entries.
 * Used from both the My Shifts page and the OEE nav bar clock-in-out button.
 */

import { Observable, of, Subject } from 'rxjs'
import { defaultIfEmpty, finalize, map, takeUntil } from 'rxjs/operators'

import { ChangeDetectorRef, Component, Inject, Injector, OnDestroy, OnInit } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog'

import { AuthenticationService } from '@services/authentication.service'
import { AvailableBatch, LaborEntryClassification, LaborEntryStationOption, OpsLaborEntry, OpsService, OpsStation, OpsStationConnection, TimeEntryAllocation } from '@services/ops.service'
import { OEEDataService } from 'src/app/features/oee-dashboard/services/oee-data.service'
import { ViewEventModalComponent } from 'src/app/features/oee-dashboard/components/modals/view-event-modal/view-event-modal.component'
import { MY_SHIFTS_ACTIVITY_CATEGORIES } from '../../my-shifts/add-shift-modal/add-shift-modal.component'
import { TimeEntrySanityCheckModalComponent, TimeEntrySanityCheckModalData, TimeEntrySanityCheckModalResult } from '../time-entry-sanity-check-modal/time-entry-sanity-check-modal.component'
import { buildJobRoutingContext, evaluateSanity, hasSanityWarnings } from '../time-entry-sanity-check-modal/time-entry-sanity'
import { submitWithReworkPrompt } from '../../my-shifts/rework-decision-modal/rework-decision.helper'

export interface EditShiftBatchContext {
    batchEventId: string
    batchNumber: string | null
    equipmentId: string | null
    equipmentName: string | null
    stationId?: string | null
    stationName?: string | null
    startTs?: string | null
    /** Product the batch belongs to — used in the off-process-flow warning copy when
     *  the underlying AvailableBatch row isn't in `this.batches` (e.g., the fallback
     *  injected by addCurrentBatchFallback carries no product_name). */
    productName?: string | null
}

export interface EditShiftModalData {
    entry: OpsLaborEntry
    /** Whether this entry is from a batch ('batch') or regular shift ('shift') */
    source?: 'shift' | 'batch'
    /** Site ID for loading notes autocomplete suggestions */
    siteId?: string
    /** Current batch context when the linked job is filtered out of available batches */
    currentBatchContext?: EditShiftBatchContext | null
    /** Active-entry edit path from My Shifts: allow edits, but do not clock out here */
    disableCompletionForActive?: boolean
    /** Job-history/batch edit paths intentionally allow completing an active entry */
    allowCompletionForActive?: boolean
    /** Operator's rolling last-30-days labor entries — used by the unusual-station warning */
    stationHistory?: OpsLaborEntry[]
}

export interface EditShiftModalResult {
    edited?: boolean
    deleted?: boolean
    clockedOut?: boolean
    duplicateEntry?: boolean
    openBatchEditor?: boolean
    batchEventId?: string
    batchContext?: EditShiftBatchContext
}

type LaborEntryUpdateBody = {
    clock_in_ts?: string
    clock_out_ts?: string | null
    notes?: string | null
    tags?: string[]
    batch_event_id?: string | null
    classification?: LaborEntryClassification
}

@Component({
    selector: 'app-edit-shift-modal',
    templateUrl: './edit-shift-modal.component.html',
    styleUrls: ['./edit-shift-modal.component.scss'],
    standalone: false
})
export class EditShiftModalComponent implements OnInit, OnDestroy {

    private destroy$ = new Subject<void>()

    notesValue = ''

    // Date objects for datetime pickers
    clockInDateObj: Date | string | null = null
    clockOutDateObj: Date | string | null = null

    // Activity/Job toggle
    editMode: 'activity' | 'job' = 'activity'

    // Job picker — shows each batch once (dedupe by batch_number). User selects the
    // batch by batch_number; the Station picker chooses which station instance of it.
    batches: AvailableBatch[] = []
    jobFilter = ''
    // Recently-closed batches (resolved/archived/aborted/completed within 7 days,
    // surfaced by the recovery arm of get_available_batches_for_site) always land
    // in the "Recently Completed" group regardless of underlying bd.status — their
    // status is misleading here (e.g., a wrong-resolved batch can still be 'idle').
    get openBatches() {
        return this.dedupeByBatchNumber(
            this.batches.filter(b => b.status !== 'idle' && !b.recently_closed_reason)
        )
    }
    get completedBatches() {
        const openNumbers = new Set(
            this.batches.filter(b => b.status !== 'idle' && !b.recently_closed_reason).map(b => b.batch_number)
        )
        return this.dedupeByBatchNumber(
            this.batches.filter(b => (b.status === 'idle' || !!b.recently_closed_reason) && !openNumbers.has(b.batch_number))
        )
    }
    get filteredOpenBatches() { return this.filterAndSortBatches(this.openBatches) }
    get filteredCompletedBatches() { return this.filterAndSortBatches(this.completedBatches) }
    selectedBatchNumber: string | null = null

    // Station picker — options are the stations this entry's batch has visited.
    // Selecting a station sets selectedStationId only. On save, we resolve the
    // batch_event_id from (selectedBatchNumber, selectedStationId) so the PUT body
    // re-links te.batch_id to the right batch_details row.
    stationOptions: LaborEntryStationOption[] = []
    stationFilter = ''
    selectedStationId: string | null = null

    // Multi-job allocations — for entries with is_multi_job = true
    isMultiJob = false
    allocations: TimeEntryAllocation[] = []
    allocationsLoading = false

    get endTimeBeforeStartTime(): boolean {
        if (!this.clockOutDateObj || !this.clockInDateObj) return false
        return new Date(this.clockOutDateObj) < new Date(this.clockInDateObj)
    }

    get filteredStations(): LaborEntryStationOption[] {
        const f = this.stationFilter.trim().toLowerCase()
        const sorted = [...this.stationOptions].sort((a, b) => a.station_name.localeCompare(b.station_name))
        return f ? sorted.filter(s => s.station_name.toLowerCase().includes(f)) : sorted
    }

    /**
     * Reverse-lookup the batch_event_id for the currently selected (batch_number, station)
     * pair. Used when building the update body.
     */
    get selectedBatchEventId(): string | null {
        if (!this.selectedBatchNumber) return null

        const originalBatchNumber = this.data.entry.batch_number ?? null
        const batchChanged = this.selectedBatchNumber !== originalBatchNumber

        if (batchChanged) {
            const selectedJobAtStation = this.selectedStationId
                ? this.batches.find(b => b.batch_number === this.selectedBatchNumber && b.equipment_id === this.selectedStationId)
                : undefined
            if (selectedJobAtStation) return selectedJobAtStation.batch_event_id

            const selectedJob = this.batches.find(b => b.batch_number === this.selectedBatchNumber)
            if (selectedJob) return selectedJob.batch_event_id
        }

        // Station options are tied to the entry's current batch. Use them only
        // when the job itself did not change; otherwise they would resolve back
        // to the old job and make a job-number edit a no-op on save.
        if (this.selectedStationId) {
            const stationMatch = this.stationOptions.find(o => o.station_id === this.selectedStationId)
            if (stationMatch) return stationMatch.batch_event_id
        }

        const batchMatch = this.batches.find(b => b.batch_number === this.selectedBatchNumber)
        return batchMatch?.batch_event_id ?? null
    }

    onStationSelected(stationId: string): void {
        this.selectedStationId = stationId
    }

    onStationPanelOpened(open: boolean): void {
        if (!open) this.stationFilter = ''
    }

    private filterAndSortBatches(list: AvailableBatch[]): AvailableBatch[] {
        const f = this.jobFilter.trim().toLowerCase()
        const sorted = [...list].sort((a, b) => a.batch_number.localeCompare(b.batch_number, undefined, { numeric: true }))
        return f ? sorted.filter(b => b.batch_number.toLowerCase().includes(f)) : sorted
    }

    private dedupeByBatchNumber(list: AvailableBatch[]): AvailableBatch[] {
        const seen = new Set<string>()
        return list.filter(b => {
            if (seen.has(b.batch_number)) return false
            seen.add(b.batch_number)
            return true
        })
    }

    activityCategories = MY_SHIFTS_ACTIVITY_CATEGORIES

    private readonly _categoryNames = new Set(['Break', 'Code Convert', 'External Support', 'Maintenance', 'Meeting', 'Training', 'Waiting', 'Waiting for Job/Parts', '6S Time', 'Lunch', 'Other'])
    private readonly _systemTags = new Set(['shift', 'labor'])

    /** The selected activity category tag */
    selectedActivityTag = ''

    // "Waiting on Parts" optional job picker
    waitingOnPartsBatches: AvailableBatch[] = []
    waitingOnPartsSearchQuery = ''
    waitingOnPartsStationId: string | null = null
    waitingOnPartsStations: OpsStation[] = []
    private originalRefBatchEventIds: string[] = []

    /** Site-level station connections used by the off-process-flow sanity check. */
    private stationConnections: OpsStationConnection[] = []

    saving = false
    clockingOut = false
    deleting = false
    showDeleteConfirm = false

    /** When checked, clocking out will resolve/complete the job at this station */
    jobCompleted = false

    /** Original values for change detection */
    private originalClockInIso: string | null = null
    private originalClockOutIso: string | null = null
    private originalNotes = ''
    private originalActivityTag = ''
    private originalClassification: LaborEntryClassification | null = null

    /** Admin correction field. Null means the authoritative work type is unavailable. */
    classification: LaborEntryClassification | null = null
    private authenticationService: AuthenticationService | null | undefined

    constructor(
        private dialogRef: MatDialogRef<EditShiftModalComponent>,
        private opsService: OpsService,
        private oeeDataService: OEEDataService,
        private injector: Injector,
        private dialog: MatDialog,
        private cdr: ChangeDetectorRef,
        @Inject(MAT_DIALOG_DATA) public data: EditShiftModalData
    ) {}

    ngOnInit(): void {
        const entry = this.data.entry

        this.notesValue = entry.notes ?? ''

        // Initialize activity tag from entry's tags
        const entryActivityTag = (entry.tags ?? []).find(t => this._categoryNames.has(t))
        this.selectedActivityTag = entryActivityTag ?? ''

        // Legacy support: if activity was in notes (pre-migration), treat it as the tag
        if (!this.selectedActivityTag && this._categoryNames.has(this.notesValue)) {
            this.selectedActivityTag = this.notesValue
            this.notesValue = ''
        }

        // Set Date objects for datetime pickers
        this.clockInDateObj = new Date(entry.clock_in_ts)
        this.clockOutDateObj = entry.clock_out_ts ? new Date(entry.clock_out_ts) : null

        // Determine initial mode based on whether entry has a batch
        this.editMode = entry.batch_number || entry.is_multi_job ? 'job' : 'activity'
        this.selectedBatchNumber = entry.batch_number ?? null
        this.isMultiJob = entry.is_multi_job ?? false
        this.initializeClassification(entry)

        // Load multi-job allocations if applicable
        if (this.isMultiJob) {
            this.allocationsLoading = true
            this.opsService.getTimeEntryAllocations(entry.id)
                .pipe(takeUntil(this.destroy$))
                .subscribe(allocations => {
                    this.allocations = allocations
                    this.allocationsLoading = false
                    this.cdr.markForCheck()
                })
        }

        // Load stations the entry's batch has visited — the station picker offers these.
        this.selectedStationId = entry.station_id ?? null
        if (entry.batch_id || entry.batch_event_id) {
            this.opsService.getLaborEntryStationOptions(entry.id)
                .pipe(takeUntil(this.destroy$))
                .subscribe(options => {
                    this.stationOptions = options
                    if (!this.selectedStationId) {
                        const entryEventId = this.getEntryBatchEventId()
                        const match = options.find(o => o.batch_event_id === entryEventId)
                            || options.find(o => o.batch_details_id === entryEventId)
                        if (match) this.selectedStationId = match.station_id
                    }
                    this.cdr.markForCheck()
                })
        }

        // Load available batches for job picker
        if (this.data.siteId) {
            this.opsService.getAvailableBatches(this.data.siteId, true)
                .pipe(takeUntil(this.destroy$))
                .subscribe(batches => {
                    this.batches = batches
                    // Ensure the entry's current batch is in the list (it may be filtered out
                    // by the available-batches query if the user already has an active entry on it)
                    if (this.selectedBatchNumber && !batches.some(b => b.batch_number === this.selectedBatchNumber)) {
                        this.addCurrentBatchFallback()
                        return
                    }
                    // Auto-select the "Waiting on Parts" reference batches from tags
                    this.autoSelectWaitingOnPartsBatches(batches)
                    this.cdr.markForCheck()
                })
        }

        // Extract original ref_batch ids from tags for change detection
        this.originalRefBatchEventIds = this.extractRefBatchEventIds(entry.tags)

        // Load stations for "Waiting on Parts" filter dropdown
        if (this.data.siteId) {
            this.opsService.getStations(this.data.siteId)
                .pipe(takeUntil(this.destroy$))
                .subscribe(stations => {
                    this.waitingOnPartsStations = stations.filter(s => !s.is_archived && s.allows_batch_operators !== false)
                    this.waitingOnPartsStationId = this.resolveLastStation(this.waitingOnPartsStations)
                    this.cdr.markForCheck()
                })
        }

        // Load site station connections — used by the off-process-flow sanity check on save.
        if (this.data.siteId) {
            this.opsService.getStationConnections(this.data.siteId)
                .pipe(takeUntil(this.destroy$))
                .subscribe(connections => {
                    this.stationConnections = connections
                })
        }

        // Store originals for change detection
        this.originalClockInIso = this.toIsoString(entry.clock_in_ts)
        this.originalClockOutIso = this.toIsoString(entry.clock_out_ts)
        this.originalNotes = this.notesValue
        this.originalActivityTag = this.selectedActivityTag

    }

    /** True if the shift is still active (no clock out) */
    get isActive(): boolean {
        return this.data.entry.clock_out_ts === null
    }

    get disableCompletionForActive(): boolean {
        return this.isActive && this.data.disableCompletionForActive === true && this.data.allowCompletionForActive !== true
    }

    /** Show the "Job completed at this station" checkbox when clocking out of a job-linked entry */
    get showJobCompletedCheckbox(): boolean {
        return this.isActive && !this.disableCompletionForActive && this.editMode === 'job' && !!this.selectedBatchNumber
    }

    get selectedStationName(): string | null {
        if (this.selectedStationId) {
            const match = this.stationOptions.find(o => o.station_id === this.selectedStationId)
            if (match) return match.station_name
        }
        return this.data.entry.station_name ?? this.data.currentBatchContext?.stationName ?? null
    }

    get selectedProductName(): string | null {
        if (!this.selectedBatchNumber) return null
        const match = this.batches.find(b => b.batch_number === this.selectedBatchNumber)
        if (match?.product_name) return match.product_name
        // The fallback batch injected by addCurrentBatchFallback carries no product_name —
        // fall back to whatever the parent passed on currentBatchContext when available.
        if (this.data.currentBatchContext?.batchNumber === this.selectedBatchNumber) {
            return this.data.currentBatchContext.productName ?? null
        }
        return null
    }

    get title(): string {
        return this.disableCompletionForActive ? 'Edit Entry' : 'Edit Shift'
    }

    get subtitle(): string {
        if (this.disableCompletionForActive) {
            return 'Update the start time or notes while keeping this entry active.'
        }

        return this.isActive
            ? 'Currently active - edit times or clock out.'
            : 'Edit clock times and notes for this shift.'
    }

    /** Auto-calculate duration from clock in to clock out (or now if active with no clock out set) */
    get calculatedDuration(): string {
        const startIso = this.toIsoString(this.clockInDateObj)
        if (!startIso) {
            return '--'
        }

        const start = new Date(startIso)
        const endIso = this.toIsoString(this.clockOutDateObj)

        if (!endIso) {
            if (this.isActive) {
                return this.formatDuration(Date.now() - start.getTime())
            }
            return '--'
        }

        const end = new Date(endIso)
        const diffMs = end.getTime() - start.getTime()

        if (diffMs < 0) {
            return 'Invalid range'
        }

        return this.formatDuration(diffMs)
    }

    /** True if any field has changed */
    get hasChanges(): boolean {
        return Object.keys(this.buildUpdateBody()).length > 0
    }

    /** Only Super Users may correct direct, single-job operator labor. */
    get canEditWorkType(): boolean {
        const entry = this.data.entry
        return this.hasUserAdminAccess
            && entry.source === 'batch'
            && this.editMode === 'job'
            && !this.isMultiJob
            && !!entry.batch_number
            && !!(entry.batch_event_id ?? entry.batch_id)
    }

    /** Keep unknown or failed-to-load classification out of the form. */
    get showWorkTypeControl(): boolean {
        return this.canEditWorkType && this.classification !== null
    }

    private get hasUserAdminAccess(): boolean {
        if (this.authenticationService === undefined) {
            try {
                this.authenticationService = this.injector.get(AuthenticationService)
            } catch {
                // Shared modal tests and read-only embeds may omit the Auth0 provider chain.
                // Fail closed so the admin-only field never appears without proven access.
                this.authenticationService = null
            }
        }
        return this.authenticationService?.hasUserAdminAccess ?? false
    }

    /** Clear the search filter when the panel closes so it's fresh next open */
    onJobPanelOpened(open: boolean): void {
        if (!open) this.jobFilter = ''
    }

    /** Select an activity category badge */
    selectCategory(name: string): void {
        if (this.selectedActivityTag === name) {
            this.selectedActivityTag = ''
            this.waitingOnPartsBatches = []
            this.waitingOnPartsSearchQuery = ''
            this.waitingOnPartsStationId = null
        } else {
            this.selectedActivityTag = name
            if (name !== 'Waiting') {
                this.waitingOnPartsBatches = []
                this.waitingOnPartsSearchQuery = ''
                this.waitingOnPartsStationId = null
            }
        }
    }

    get isWaitingOnPartsSelected(): boolean {
        return this.editMode === 'activity' && this.selectedActivityTag === 'Waiting'
    }

    get filteredWaitingOnPartsBatches(): AvailableBatch[] {
        let results = this.batches

        if (this.waitingOnPartsStationId) {
            results = results.filter(b => b.equipment_id === this.waitingOnPartsStationId)
        }

        const query = this.waitingOnPartsSearchQuery.trim().toLowerCase()
        if (query) {
            results = results.filter(batch =>
                batch.batch_number.toLowerCase().includes(query)
                || (batch.work_order?.toLowerCase().includes(query) ?? false)
                || batch.equipment_name.toLowerCase().includes(query)
                || (batch.product_name?.toLowerCase().includes(query) ?? false)
            )
        }

        return results.slice(0, 20)
    }

    isWaitingBatchSelected(batch: AvailableBatch): boolean {
        return this.waitingOnPartsBatches.some(b => b.batch_event_id === batch.batch_event_id)
    }

    toggleWaitingOnPartsBatch(batch: AvailableBatch): void {
        if (this.isWaitingBatchSelected(batch)) {
            this.waitingOnPartsBatches = this.waitingOnPartsBatches.filter(b => b.batch_event_id !== batch.batch_event_id)
        } else {
            this.waitingOnPartsBatches = [...this.waitingOnPartsBatches, batch]
        }
    }

    getBatchSubtitle(batch: AvailableBatch): string {
        const parts: string[] = []
        if (batch.product_name) {
            parts.push(batch.product_name)
            if (batch.equipment_name) {
                parts.push(`Last at ${batch.equipment_name}`)
            }
        } else if (batch.equipment_name) {
            parts.push(batch.equipment_name)
        }
        if (batch.work_order) {
            parts.push(`WO ${batch.work_order}`)
        }
        return parts.join(' \u00b7 ')
    }

    /**
     * Pill label / tooltip for a recently-closed batch surfaced by the 7-day
     * recovery arm of get_available_batches_for_site (#3160).
     */
    getRecentlyClosedPill(batch: AvailableBatch): { label: string, tooltip: string } | null {
        const reason = batch.recently_closed_reason
        if (!reason) {
            return null
        }
        const labels: Record<NonNullable<typeof reason>, string> = {
            resolved: 'Recently resolved',
            archived: 'Recently archived',
            aborted: 'Recently aborted',
            completed: 'Recently completed'
        }
        return {
            label: labels[reason],
            tooltip: 'This job was closed. Selecting it will reopen it for your shift.'
        }
    }

    private extractRefBatchEventIds(tags: string[] | null): string[] {
        return (tags ?? []).filter(t => t.startsWith('ref_batch:')).map(t => t.substring('ref_batch:'.length))
    }

    private resolveLastStation(stations: OpsStation[]): string | null {
        try {
            const stored = localStorage.getItem(`my_shifts_last_add_time_station_id:${this.data.siteId}`)
            if (stored && stations.some(s => s.id === stored)) return stored
        } catch { /* ignore */ }
        return null
    }

    private autoSelectWaitingOnPartsBatches(batches: AvailableBatch[]): void {
        const refIds = this.originalRefBatchEventIds
        if (refIds.length === 0 || !this.isWaitingOnPartsSelected) return

        this.waitingOnPartsBatches = batches.filter(b => refIds.includes(b.batch_event_id))
    }

    /** Check if a value is a known activity category */
    isActivityCategory(value: string): boolean {
        return this._categoryNames.has(value)
    }

    /** Save changed fields */
    onSave(): void {
        if (this.saving) return
        if (this.endTimeBeforeStartTime) return

        // Synchronous path when there are no warnings so existing onSave assertions still hold.
        if (this.openSanityCheckIfNeeded('edit', () => this.doSave())) return
        this.doSave()
    }

    private doSave(): void {
        this.saving = true

        this.resolveSelectedStationToBatchEvent().subscribe({
            next: ensuredBatchEventId => {
                const body = this.buildUpdateBody()
                if (ensuredBatchEventId) body.batch_event_id = ensuredBatchEventId
                const batchEventId = this.getResultingBatchEventId(body)
                const shouldOpenBatchEditor = this.shouldOpenBatchEditorAfterSave(body, batchEventId)

                const classificationOnly = Object.keys(body).length === 1 && body.classification !== undefined
                const saveRequest: Observable<OpsLaborEntry | undefined> = classificationOnly
                    ? this.opsService.updateLaborEntry(this.data.entry.id, body)
                    : submitWithReworkPrompt(this.dialog, (isRework) =>
                        this.opsService.updateLaborEntry(this.data.entry.id, { ...body, is_rework: isRework })
                    )

                saveRequest.pipe(takeUntil(this.destroy$))
                    .subscribe({
                        next: (result) => {
                            this.saving = false
                            if (!result) {
                                this.cdr.markForCheck()
                                return
                            }
                            this.dialogRef.close({
                                edited: true,
                                openBatchEditor: shouldOpenBatchEditor,
                                batchEventId: shouldOpenBatchEditor ? batchEventId ?? undefined : undefined,
                                batchContext: shouldOpenBatchEditor ? this.getSelectedBatchContext(batchEventId) ?? undefined : undefined
                            } as EditShiftModalResult)
                        },
                        error: () => {
                            this.saving = false
                            this.cdr.markForCheck()
                        }
                    })
            },
            error: () => {
                this.saving = false
                this.cdr.markForCheck()
            }
        })
    }

    /**
     * If the user picked a station the batch hasn't been at yet, ensure the batch
     * is routed there (creates a new batch_details + machine_event) and return the
     * new batch_event_id so onSave can re-link te.batch_id to it. Otherwise emits
     * null and the existing batch_event_id is used.
     */
    private resolveSelectedStationToBatchEvent(): Observable<string | null> {
        if (!this.selectedStationId) return of(null)
        const match = this.stationOptions.find(o => o.station_id === this.selectedStationId)
        if (!match || match.batch_event_id) return of(null)
        return this.opsService.ensureBatchAtStation({
            equipment_id: match.station_id,
            source_batch_event_id: match.source_batch_event_id,
            // Route-only: we're re-linking an existing entry, not clocking in fresh. Without
            // this the server auto-starts a new NOW-stamped entry and the user ends up with
            // two rows on the target station for the same job.
            skip_time_entries: true
        }).pipe(
            map(result => {
                // Patch the in-memory station options so a subsequent re-open reflects reality.
                match.batch_event_id = result.batch_event_id
                match.batch_details_id = result.batch_details_id
                return result.batch_event_id
            })
        )
    }

    /** Clock out the active shift */
    onClockOut(): void {
        if (this.clockingOut) return

        if (this.openSanityCheckIfNeeded('edit', () => this.doClockOut())) return
        this.doClockOut()
    }

    private doClockOut(): void {
        this.clockingOut = true

        const body = this.buildUpdateBody()
        const hasEdits = Object.keys(body).length > 0
        const batchEventId = this.getResultingBatchEventId(hasEdits ? body : undefined)

        const doClockOut = () => {
            // When "job completed" is checked, use completeLaborEntry to trigger
            // resolve/complete logic on the batch at this station
            if (this.jobCompleted && batchEventId) {
                const clockOutIso = this.getClockOutIsoForSubmit()
                const clockInIso = this.toIsoString(this.clockInDateObj) || undefined
                this.opsService.completeLaborEntry(this.data.entry.id, {
                    clock_in_ts: clockInIso,
                    clock_out_ts: clockOutIso,
                    notes: this.notesValue?.trim() || null,
                    complete_job: this.jobCompleted
                })
                    .pipe(takeUntil(this.destroy$))
                    .subscribe({
                        next: () => {
                            this.clockingOut = false
                            this.dialogRef.close({
                                clockedOut: true,
                                batchEventId: batchEventId ?? undefined,
                                batchContext: this.getSelectedBatchContext(batchEventId) ?? undefined
                            } as EditShiftModalResult)
                        },
                        error: () => {
                            this.clockingOut = false
                            this.cdr.markForCheck()
                        }
                    })
                return
            }

            this.opsService.clockOut(this.data.entry.id, this.getClockOutIsoForSubmit())
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                    next: () => {
                        this.clockingOut = false
                        this.dialogRef.close({
                            clockedOut: true,
                            openBatchEditor: !!batchEventId,
                            batchEventId: batchEventId ?? undefined,
                            batchContext: this.getSelectedBatchContext(batchEventId) ?? undefined
                        } as EditShiftModalResult)
                    },
                    error: () => {
                        this.clockingOut = false
                        this.cdr.markForCheck()
                    }
                })
        }

        if (hasEdits) {
            this.opsService.updateLaborEntry(this.data.entry.id, body)
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                    next: () => doClockOut(),
                    error: () => {
                        this.clockingOut = false
                        this.cdr.markForCheck()
                    }
                })
        } else {
            doClockOut()
        }
    }

    /** Delete the shift entry */
    onDelete(): void {
        if (this.deleting) return

        this.deleting = true

        this.opsService.deleteLaborEntry(this.data.entry.id)
            .pipe(takeUntil(this.destroy$))
            .pipe(defaultIfEmpty(undefined))
            .pipe(finalize(() => {
                this.deleting = false
                this.cdr.markForCheck()
            }))
            .subscribe({
                next: () => {
                    this.dialogRef.close({ deleted: true } as EditShiftModalResult)
                },
                error: () => {
                    // finalize handles state reset
                }
            })
    }

    /** Close without saving */
    onCancel(): void {
        this.dialogRef.close()
    }

    /** Close this modal and signal parent to open the Edit Batch modal */
    openEditBatchModal(): void {
        const batchEventId = this.getResultingBatchEventId()
        this.dialogRef.close({
            openBatchEditor: true,
            batchEventId: batchEventId ?? undefined,
            batchContext: this.getSelectedBatchContext(batchEventId) ?? undefined
        } as EditShiftModalResult)
    }

    onDuplicateEntry(): void {
        this.dialogRef.close({ duplicateEntry: true } as EditShiftModalResult)
    }

    /** Toggle the delete confirmation UI */
    toggleDeleteConfirm(): void {
        this.showDeleteConfirm = !this.showDeleteConfirm
    }

    /**
     * If the pending change has no duration/station warnings, returns false so the caller can
     * proceed synchronously. Otherwise opens the warning dialog and invokes onProceed only if
     * the user confirms; returns true so the caller knows to bail out of its own flow.
     */
    private openSanityCheckIfNeeded(action: 'add' | 'edit', onProceed: () => void): boolean {
        const candidate = {
            id: this.data.entry.id,
            clockInIso: this.toIsoString(this.clockInDateObj) ?? this.data.entry.clock_in_ts,
            clockOutIso: this.disableCompletionForActive
                ? this.data.entry.clock_out_ts
                : this.toIsoString(this.clockOutDateObj),
            stationId: this.selectedStationId,
            stationName: this.selectedStationName,
            batchNumber: this.selectedBatchNumber,
            productName: this.selectedProductName,
            stationAllowsConcurrent: this.stationOptions.find(o => o.station_id === this.selectedStationId)?.allow_concurrent_jobs ?? false
        }
        // Routing checks only apply to job entries; activity edits get the existing day/station checks only.
        // Exclude the entry's own original batch_event so a station change isn't flagged as
        // "active elsewhere" against its own pre-edit location.
        const excludeBatchEventId = this.data.entry.batch_event_id ?? this.data.entry.batch_id ?? null
        const routing = (this.editMode === 'job' && this.selectedBatchNumber)
            ? buildJobRoutingContext(this.batches, this.stationConnections, this.selectedBatchNumber, excludeBatchEventId)
            : undefined
        const result = evaluateSanity(candidate, this.data.stationHistory ?? [], routing)
        if (!hasSanityWarnings(result)) return false

        const ref = this.dialog.open<TimeEntrySanityCheckModalComponent, TimeEntrySanityCheckModalData, TimeEntrySanityCheckModalResult>(
            TimeEntrySanityCheckModalComponent,
            {
                width: '450px',
                maxWidth: '95vw',
                panelClass: 'oee-mobile-modal',
                data: { ...result, action }
            }
        )
        ref.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(res => {
            if (res?.proceed) onProceed()
        })
        return true
    }

    private toIsoString(value: Date | string | null | undefined): string | null {
        if (!value) {
            return null
        }

        const date = value instanceof Date ? value : new Date(value)
        return Number.isNaN(date.getTime()) ? null : date.toISOString()
    }

    private getClockOutIsoForSubmit(): string {
        const selectedClockOutIso = this.toIsoString(this.clockOutDateObj)
        if (selectedClockOutIso) {
            return selectedClockOutIso
        }

        let fallback = this.roundToNearestQuarterHour(new Date())
        const clockInIso = this.toIsoString(this.clockInDateObj)
        if (clockInIso) {
            const clockIn = new Date(clockInIso)
            if (fallback < clockIn) {
                fallback = clockIn
            }
        }

        return fallback.toISOString()
    }

    private roundToNearestQuarterHour(value: Date): Date {
        const rounded = new Date(value)
        const roundedMinutes = Math.round(rounded.getMinutes() / 15) * 15
        rounded.setMinutes(roundedMinutes, 0, 0)
        return rounded
    }

    openJobDetails(alloc: TimeEntryAllocation): void {
        this.oeeDataService.getEventById(alloc.batch_event_id).pipe(
            takeUntil(this.destroy$)
        ).subscribe(event => {
            if (event) {
                this.dialog.open(ViewEventModalComponent, {
                    width: '700px',
                    maxHeight: '90vh',
                    panelClass: 'oee-mobile-modal',
                    disableClose: true,
                    data: { ...event, _readOnly: true }
                })
            }
        })
    }

    ngOnDestroy(): void {
        this.destroy$.next()
        this.destroy$.complete()
    }

    private buildUpdateBody(): LaborEntryUpdateBody {
        const body: LaborEntryUpdateBody = {}
        const currentClockInIso = this.toIsoString(this.clockInDateObj)
        const currentClockOutIso = this.toIsoString(this.clockOutDateObj)

        if (currentClockInIso && currentClockInIso !== this.originalClockInIso) {
            body.clock_in_ts = currentClockInIso
        }

        if (!this.disableCompletionForActive && currentClockOutIso !== this.originalClockOutIso) {
            body.clock_out_ts = currentClockOutIso ?? null
        }

        if (this.notesValue !== this.originalNotes) {
            body.notes = this.notesValue || null
        }

        // Build tags: system tags + activity tag + ref_batch tags
        const currentRefBatchEventIds = this.waitingOnPartsBatches.map(b => b.batch_event_id)
        const refBatchChanged = JSON.stringify(currentRefBatchEventIds.sort()) !== JSON.stringify([...this.originalRefBatchEventIds].sort())
        if (this.selectedActivityTag !== this.originalActivityTag || refBatchChanged) {
            const existingSystemTags = (this.data.entry.tags ?? []).filter(t => this._systemTags.has(t))
            const tags = this.selectedActivityTag
                ? [...existingSystemTags, this.selectedActivityTag]
                : (existingSystemTags.length > 0 ? existingSystemTags : ['shift'])
            for (const id of currentRefBatchEventIds) {
                tags.push(`ref_batch:${id}`)
            }
            body.tags = tags
        }

        // Emit batch_event_id only when the logical selection actually changed —
        // either a different batch_number or a different station (which maps to a
        // sibling batch_event_id on the same batch). Comparing the raw getter value
        // to the entry's original batch_event_id is wrong: the stationOptions query
        // picks the most recent machine_event at each station, which may differ from
        // whatever older event the entry originally pointed at, and would cause a
        // phantom re-link on every save.
        const originalBatchNumber = this.data.entry.batch_number ?? null
        const originalStationId = this.data.entry.station_id ?? null
        const batchChanged = this.selectedBatchNumber !== originalBatchNumber
        const stationChanged = this.selectedStationId !== originalStationId && this.selectedStationId !== null
        if (batchChanged || stationChanged) {
            body.batch_event_id = this.selectedBatchEventId
        }

        if (
            this.canEditWorkType
            && this.classification !== null
            && this.originalClassification !== null
            && this.classification !== this.originalClassification
        ) {
            body.classification = this.classification
        }

        return body
    }

    private initializeClassification(entry: OpsLaborEntry): void {
        this.classification = null
        this.originalClassification = null

        if (!this.canEditWorkType) return

        if (typeof entry.is_rework === 'boolean') {
            this.classification = entry.is_rework ? 'rework' : 'standard'
            this.originalClassification = this.classification
            return
        }

        this.opsService.getLaborEntryById(entry.id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: hydratedEntry => {
                    if (typeof hydratedEntry.is_rework === 'boolean') {
                        entry.is_rework = hydratedEntry.is_rework
                        this.classification = hydratedEntry.is_rework ? 'rework' : 'standard'
                        this.originalClassification = this.classification
                    }
                    this.cdr.markForCheck()
                },
                error: () => {
                    // Unknown is intentionally not treated as Standard.
                    this.cdr.markForCheck()
                }
            })
    }

    private getResultingBatchEventId(body?: LaborEntryUpdateBody): string | null {
        if (body && Object.prototype.hasOwnProperty.call(body, 'batch_event_id')) {
            return body.batch_event_id ?? null
        }

        return this.selectedBatchEventId ?? this.data.entry.batch_id ?? null
    }

    private shouldOpenBatchEditorAfterSave(body: LaborEntryUpdateBody, batchEventId: string | null): boolean {
        if (!batchEventId || !this.isActive) {
            return false
        }

        return typeof body.clock_out_ts === 'string' && body.clock_out_ts.length > 0
    }

    getBatchContextName(batch: AvailableBatch): string | null {
        const contextName = batch.equipment_name?.trim()
        return contextName ? contextName : null
    }

    private addCurrentBatchFallback(): void {
        const fallbackEventId = this.getEntryBatchEventId()
        if (!fallbackEventId || !this.selectedBatchNumber) {
            return
        }

        const currentBatchContext = this.data.currentBatchContext
        this.batches.unshift(this.buildFallbackBatch(
            fallbackEventId,
            currentBatchContext?.equipmentName ?? currentBatchContext?.stationName ?? null,
            currentBatchContext?.equipmentId ?? currentBatchContext?.stationId ?? ''
        ))
        this.cdr.markForCheck()
    }

    private getSelectedBatchContext(batchEventId: string | null): EditShiftBatchContext | null {
        if (!batchEventId) {
            return null
        }

        const selectedBatch = this.batches.find(batch => batch.batch_event_id === batchEventId)
        if (selectedBatch) {
            const currentBatchContext = this.data.currentBatchContext?.batchEventId === batchEventId
                ? this.data.currentBatchContext
                : null
            return {
                batchEventId,
                batchNumber: selectedBatch.batch_number ?? this.data.entry.batch_number ?? null,
                equipmentId: selectedBatch.equipment_id || null,
                equipmentName: selectedBatch.equipment_name?.trim() || null,
                stationId: currentBatchContext?.stationId ?? null,
                stationName: currentBatchContext?.stationName ?? null,
                startTs: selectedBatch.started_at ?? this.data.entry.clock_in_ts
            }
        }

        if (this.data.currentBatchContext?.batchEventId === batchEventId) {
            return {
                ...this.data.currentBatchContext,
                batchNumber: this.data.currentBatchContext.batchNumber ?? this.data.entry.batch_number ?? null,
                startTs: this.data.currentBatchContext.startTs ?? this.data.entry.clock_in_ts
            }
        }

        return {
            batchEventId,
            batchNumber: this.data.entry.batch_number ?? null,
            equipmentId: null,
            equipmentName: null,
            startTs: this.data.entry.clock_in_ts
        }
    }

    private buildFallbackBatch(batchEventId: string, contextName: string | null, equipmentId: string): AvailableBatch {
        return {
            batch_event_id: batchEventId,
            batch_number: this.data.entry.batch_number || 'Current Job',
            work_order: null,
            equipment_id: equipmentId,
            equipment_name: contextName?.trim() ?? '',
            started_at: this.data.entry.clock_in_ts,
            operator_count: 0,
            status: 'running'
        }
    }

    private formatDuration(ms: number): string {
        const totalMinutes = Math.floor(ms / 60000)
        const hours = Math.trunc(totalMinutes / 60)
        const minutes = totalMinutes % 60
        return `${hours}h ${minutes}m`
    }

    private getEntryBatchEventId(): string | null {
        return this.data.entry.batch_event_id ?? this.data.entry.batch_id ?? null
    }
}
